import React, { useState, useMemo } from 'react';
import { DropdownMenu, Button, SparkIcon, Label, sparkToast, TruncatedText } from '@spark';
import SpecificAILogo from '@/shared/assets/SpecificAILogo';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { getLanguageNameByCode } from '@/shared/constants/languageConstants';
import { useAuth } from '@/features/auth/AuthContext';
import { ModelProcessingModal } from '@/features/tasks';
import EvaluationRunStatus from './EvaluationRunStatus';
import RecalculateEvaluationParamsModal from './RecalculateEvaluationParamsModal';

const FilterBar = ({
    compareOptions,
    versionOptions,
    selectedVersion,
    setSelectedVersion,
    currentToCompare,
    setCurrentToCompare,
    selectedLabel,
    setSelectedLabel,
    languageOptions = null,
    selectedLanguage = null,
    setSelectedLanguage = null,
    hideLanguageSelect = false,
    selectedMatchType = null,
    setSelectedMatchType = null,
    getClassOptions,
    dropdownLabelRender,
    onRefreshStart,
    onRefreshComplete,
    localUsecase,
    versionOptionsRaw,
    viewMode,
    setViewMode,
    moveToScreen,
    onImproveClick,
    showImproveEvals = true,
    improveButtonLabel = "Improve",
    hideLabelSelect = false,
    hideViewToggle = false,
    taskType,
    activeTab = 'entities',
    onPlayWithThreshold,
    isThresholdMode = false,
    onBackFromThreshold,
    runDateIso = null,
    isLatestEvaluation = null,
    runStatusVersion = null,
    onSwitchToLatest = null,
    distillationEventId = null,
    // Map of major version (number) -> list of its evaluation-run minor versions
    // (e.g. { 5: [5.0, 5.01, 5.02] }); majors with more than one run get the
    // "replace benchmark" affordance in the "To" dropdown.
    minorVersionsByMajor = null,
    // Map of major version (number) -> the minor version whose benchmark is
    // currently active for that major (session lock or latest run).
    activeMinorByMajor = null,
    // Opens the "Replace Benchmark" modal for the given major version.
    onReplaceBenchmark = null,
}) => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [paramsModalOpen, setParamsModalOpen] = useState(false);
    const [reevalStartedModalOpen, setReevalStartedModalOpen] = useState(false);
    const [compareMenuOpen, setCompareMenuOpen] = useState(false);
    const [versionMenuOpen, setVersionMenuOpen] = useState(false);
    const { role, user } = useAuth();
    const isSummarization = isGenerativeTask(taskType);
    const isNerTaskType = taskType === TaskType.NER;
    const latestVersion = versionOptionsRaw && versionOptionsRaw.length > 0 ? Math.max(...versionOptionsRaw) : null;
    const selectedMajorVersion = selectedVersion ? Math.floor(selectedVersion) : null;
    const latestMajorVersion = latestVersion ? Math.floor(latestVersion) : null;
    const isLatestMajor = selectedMajorVersion === latestMajorVersion;

    const majorVersionOptions = useMemo(() => (versionOptionsRaw || [])
        .map(v => Math.floor(Number(v)))
        .filter(v => Number.isFinite(v))
        .filter((v, idx, arr) => arr.indexOf(v) === idx)
        .sort((a, b) => b - a)
        .map(v => ({ label: `V${v}`, value: v })), [versionOptionsRaw]);

    // Returns "v5.02"-style label of the active benchmark run for a major
    // version, but only when that major has more than one evaluation run.
    const getActiveMinorLabel = useMemo(() => (major) => {
        const minors = minorVersionsByMajor?.[major] ?? minorVersionsByMajor?.[String(major)];
        if (!Array.isArray(minors) || minors.length <= 1) return null;
        const active = activeMinorByMajor?.[major]
            ?? activeMinorByMajor?.[String(major)]
            ?? Math.max(...minors.map(Number));
        const num = Number(active);
        if (!Number.isFinite(num)) return null;
        return Number.isInteger(num) ? `v${num}.0` : `v${num}`;
    }, [minorVersionsByMajor, activeMinorByMajor]);

    const filteredCompareOptions = useMemo(() => {
        const options = (compareOptions || []).filter(option =>
            option.value === 'oldModel' ||
            option.type === 'results_file' ||
            (option.type === 'optune' && option.value !== selectedVersion)
        );
        return options;
    }, [compareOptions, selectedVersion]);

    const handleCompareOpenChange = (nextOpen) => {
        if (nextOpen && filteredCompareOptions.length === 0) {
            sparkToast.error('Nothing to compare');
            setCompareMenuOpen(false);
            return;
        }
        setCompareMenuOpen(nextOpen);
    };

    const compareItems = useMemo(() =>
        filteredCompareOptions.map(option => ({
            label: String(option.label),
            state: option.value === currentToCompare ? 'selected' : 'default',
        })), [filteredCompareOptions, currentToCompare]);

    const compareDisplayText = useMemo(() => {
        if (currentToCompare == null) return undefined;
        if (currentToCompare === 'oldModel' || currentToCompare === 'file' || typeof currentToCompare === 'number') {
            const found = filteredCompareOptions.find(o => o.value === currentToCompare);
            return found ? String(found.label) : String(currentToCompare);
        }
        return String(currentToCompare);
    }, [currentToCompare, filteredCompareOptions]);

    const versionItems = useMemo(() =>
        majorVersionOptions.map(v => {
            const minorLabel = getActiveMinorLabel(v.value);
            return {
                label: minorLabel ? `${v.label} (${minorLabel})` : v.label,
                state: v.value === (selectedMajorVersion ?? selectedVersion) ? 'selected' : 'default',
                suffix: minorLabel && typeof onReplaceBenchmark === 'function' ? (
                    <Button
                        variant="ghost"
                        size="tiny"
                        tooltip="Replace Benchmark"
                        aria-label={`Replace benchmark for ${v.label}`}
                        iconSlug="switch-horizontal"
                        startIcon={<SparkIcon slug="switch-horizontal" size="small" />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setVersionMenuOpen(false);
                            onReplaceBenchmark(v.value);
                        }}
                    />
                ) : undefined,
            };
        }), [majorVersionOptions, selectedMajorVersion, selectedVersion, getActiveMinorLabel, onReplaceBenchmark]);

    const versionDisplayText = useMemo(() => {
        const val = selectedMajorVersion ?? selectedVersion;
        if (val == null) return undefined;
        const found = majorVersionOptions.find(v => v.value === val);
        const baseLabel = found ? found.label : `V${val}`;
        const minorLabel = getActiveMinorLabel(val);
        return minorLabel ? `${baseLabel} (${minorLabel})` : baseLabel;
    }, [selectedMajorVersion, selectedVersion, majorVersionOptions, getActiveMinorLabel]);

    const sortedLanguageOptions = useMemo(() => {
        if (!Array.isArray(languageOptions) || languageOptions.length === 0) return [];
        return [
            { value: 'all', label: 'All languages' },
            ...[...languageOptions]
                .sort((a, b) =>
                    getLanguageNameByCode(String(a)).localeCompare(getLanguageNameByCode(String(b)))
                )
                .map(lang => ({ value: lang, label: getLanguageNameByCode(String(lang)) })),
        ];
    }, [languageOptions]);

    const languageItems = useMemo(() =>
        sortedLanguageOptions.map(opt => ({
            label: opt.label,
            state: opt.value === (selectedLanguage ?? 'all') ? 'selected' : 'default',
        })), [sortedLanguageOptions, selectedLanguage]);

    const languageDisplayText = useMemo(() => {
        if (selectedLanguage == null) return 'All languages';
        const found = sortedLanguageOptions.find(o => o.value === selectedLanguage);
        return found ? found.label : String(selectedLanguage);
    }, [selectedLanguage, sortedLanguageOptions]);

    const classOptionsData = useMemo(() => {
        if (hideLabelSelect || typeof getClassOptions !== 'function') return [];
        return getClassOptions();
    }, [hideLabelSelect, getClassOptions]);

    const labelItems = useMemo(() =>
        classOptionsData.map(option => ({
            label: `${option.label}${option.count != null ? ` (${option.count})` : ''}`,
            state: option.value === selectedLabel ? 'selected' : 'default',
        })), [classOptionsData, selectedLabel]);

    const labelDisplayText = useMemo(() => {
        if (selectedLabel == null) return undefined;
        const found = classOptionsData.find(o => o.value === selectedLabel);
        return found ? String(found.label) : String(selectedLabel);
    }, [selectedLabel, classOptionsData]);

    const matchTypeItems = useMemo(() => [
        { label: 'Exact match', state: selectedMatchType === 'exact' ? 'selected' : 'default' },
        { label: 'Partial entity detection', state: selectedMatchType === 'partial' ? 'selected' : 'default' },
    ], [selectedMatchType]);

    const matchTypeDisplayText = useMemo(() => {
        if (selectedMatchType === 'exact') return 'Exact match';
        if (selectedMatchType === 'partial') return 'Partial entity detection';
        return undefined;
    }, [selectedMatchType]);

    const openParamsModal = () => {
        if (!localUsecase || !selectedVersion || !isLatestMajor || isRefreshing) return;
        setParamsModalOpen(true);
    };

    const handleRefresh = async ({ includeInstructionInTraining } = {}) => {
        if (!localUsecase || !selectedVersion || !isLatestMajor) return;
        setParamsModalOpen(false);

        try {
            setIsRefreshing(true);
            onRefreshStart?.();

            const body = {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                version: selectedVersion,
            };
            if (typeof includeInstructionInTraining === 'boolean') {
                body.include_instruction_in_training = includeInstructionInTraining;
            }

            const response = await data_from_server_post('recalculate-evaluation', body);

            if (response?.success) {
                const newVersion = response?.new_version ?? null;
                sparkToast.success("Re evaluation triggered successfully");
                // Same heads-up as training: "Revaluate Has Started!" with an
                // optional email notification for when it finishes.
                setReevalStartedModalOpen(true);
                if (typeof newVersion === "number" && Number.isFinite(newVersion)) {
                    setSelectedVersion(Math.floor(newVersion));
                } else if (typeof selectedMajorVersion === "number" && Number.isFinite(selectedMajorVersion)) {
                    setSelectedVersion(selectedMajorVersion);
                }
                onRefreshComplete?.({ success: true, response });
            } else {
                sparkToast.error(response?.message || 'Failed to trigger evaluation recalculation');
                onRefreshComplete?.({ success: false, response });
            }
        } catch (error) {
            console.error('Error triggering evaluation recalculation:', error);
            sparkToast.error('Failed to trigger evaluation recalculation');
            onRefreshComplete?.({ success: false, error });
        } finally {
            setIsRefreshing(false);
        }
    };

    // "Notify Me" on the re-evaluation heads-up — register the logged-in
    // user's email for a completion email (backend_consumer sends it when the
    // recalculation reaches a terminal state). Only offered when we actually
    // have an email (auth-disabled sessions have an empty one).
    const canNotifyByEmail = Boolean(user?.email && localUsecase?._id);
    const handleNotifyMeReevaluation = () => {
        setReevalStartedModalOpen(false);
        data_from_server_post(
            'notify_on_evaluation_completion',
            {
                client_id: localUsecase?.client_id,
                usecase_id: localUsecase?._id,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success("We'll email you when the re-evaluation is done");
                } else if (result?.message) {
                    sparkToast.error(result.message);
                }
            },
            () => sparkToast.error('Failed to register the notification')
        );
    };

    const shouldShowImproveButton = showImproveEvals && !isSummarization && !isNerTaskType;

    const getLabelPlaceholder = () => {
        if (taskType === TaskType.NER_WITH_RELATIONS) {
            return activeTab === 'entities' ? 'All entities' : 'All relations';
        }
        return taskType === TaskType.NER ? 'All entities' : 'All labels';
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            marginTop: 0,
            marginBottom: 32,
            borderRadius: 12,
            gap: 12,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: 16,
            }}>
                {isThresholdMode && (
                    <button
                        type="button"
                        className="fs-unmask"
                        onClick={onBackFromThreshold}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            background: 'none',
                            border: 'none',
                            padding: 4,
                            cursor: 'pointer',
                            flexShrink: 0,
                            color: 'var(--semantic-content-primary, #1a1b23)',
                            fontFamily: "var(--font-family-platform, 'Figtree', sans-serif)",
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: '22px',
                        }}
                    >
                        <SparkIcon slug="arrow-left" size="small" />
                        <span>Back</span>
                    </button>
                )}
                <EvaluationRunStatus
                    runDateIso={runDateIso}
                    isLatest={isLatestEvaluation}
                    version={runStatusVersion}
                    onSwitchToLatest={onSwitchToLatest}
                />
                {!isThresholdMode && (
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0, gap: 10 }}>
                    {typeof onPlayWithThreshold === 'function' && (
                        <Button
                            className="fs-unmask"
                            variant="secondary"
                            size="medium"
                            showStartIcon
                            startIcon={<SparkIcon slug="adjust" size="medium" />}
                            onClick={onPlayWithThreshold}
                        >
                            Adjust Threshold
                        </Button>
                    )}
                    <Button
                        className="fs-unmask"
                        variant="secondary"
                        size="medium"
                        showStartIcon
                        startIcon={<SparkIcon slug="report-refresh" size="medium" />}
                        onClick={openParamsModal}
                        disabled={!isLatestMajor || isRefreshing}
                        disableWhenReadOnly
                    >
                        Revaluate
                    </Button>
                    {shouldShowImproveButton && (
                        <Button
                            className="fs-unmask"
                            variant="secondary"
                            size="medium"
                            showStartIcon
                            startIcon={<SparkIcon slug="trending-up" size="medium" />}
                            onClick={() => (onImproveClick ? onImproveClick() : moveToScreen?.(localUsecase?._id, "data-selection", true))}
                            disableWhenReadOnly
                        >
                            {improveButtonLabel}
                        </Button>
                    )}
                </div>
                )}
            </div>
            <div style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'nowrap',
                alignItems: 'flex-end',
            }}>
                <div className="fs-unmask">
                    <DropdownMenu
                        size="medium"
                        label="Compare"
                        placeholder="Compare with"
                        text={compareDisplayText}
                        showLabel
                        items={compareItems}
                        value={compareDisplayText}
                        showClearButton
                        open={compareMenuOpen}
                        onOpenChange={handleCompareOpenChange}
                        onClear={() => setCurrentToCompare(null)}
                        onSelect={(item, index) => {
                            const opt = filteredCompareOptions[index];
                            if (!opt) return;
                            setCurrentToCompare(opt.value ?? null);
                        }}
                    />
                </div>
                <div>
                    <DropdownMenu
                        size="medium"
                        label="To"
                        placeholder="Version"
                        text={versionDisplayText}
                        showLabel
                        items={versionItems}
                        panelTitle="Versions"
                        value={versionDisplayText}
                        open={versionMenuOpen}
                        onOpenChange={setVersionMenuOpen}
                        onSelect={(item, index) => {
                            const opt = majorVersionOptions[index];
                            if (opt) setSelectedVersion(Math.floor(Number(opt.value)));
                        }}
                    />
                </div>
                {!hideLanguageSelect &&
                    Array.isArray(languageOptions) &&
                    languageOptions.length > 0 &&
                    typeof setSelectedLanguage === 'function' && (
                        <DropdownMenu
                            size="medium"
                            label="Language"
                            placeholder="All languages"
                            text={languageDisplayText}
                            showLabel
                            items={languageItems}
                            value={languageDisplayText}
                            onSelect={(item, index) => {
                                const opt = sortedLanguageOptions[index];
                                if (opt) setSelectedLanguage(opt.value === 'all' ? null : opt.value);
                            }}
                        />
                    )}
                {!hideLabelSelect && (
                    <DropdownMenu
                        size="medium"
                        label={taskType === TaskType.NER_WITH_RELATIONS
                            ? (activeTab === 'entities' ? 'Entity types' : 'Relations')
                            : (taskType === TaskType.NER ? 'Entity types' : 'Labels')}
                        placeholder={getLabelPlaceholder()}
                        text={labelDisplayText}
                        showLabel
                        items={labelItems}
                        value={labelDisplayText}
                        onSelect={(item, index) => {
                            const opt = classOptionsData[index];
                            if (opt) setSelectedLabel(opt.value);
                        }}
                    />
                )}
                {!hideLabelSelect && (taskType === TaskType.NER || (taskType === TaskType.NER_WITH_RELATIONS && activeTab === "entities")) && (
                    <DropdownMenu
                        size="medium"
                        label="Entity detection"
                        placeholder="Select entity match type"
                        text={matchTypeDisplayText}
                        showLabel
                        items={matchTypeItems}
                        value={matchTypeDisplayText}
                        onSelect={(item) => {
                            const val = item.label === 'Exact match' ? 'exact' : 'partial';
                            setSelectedMatchType(val);
                        }}
                    />
                )}
            </div>
            <RecalculateEvaluationParamsModal
                open={paramsModalOpen}
                usecase={localUsecase}
                distillationEventId={distillationEventId}
                confirming={isRefreshing}
                onCancel={() => setParamsModalOpen(false)}
                onConfirm={handleRefresh}
                onGoToModelConfiguration={() => {
                    setParamsModalOpen(false);
                    moveToScreen?.(localUsecase?._id, 'model-setup');
                }}
            />
            <ModelProcessingModal
                title="Revaluate Has Started!"
                open={reevalStartedModalOpen}
                onClose={() => setReevalStartedModalOpen(false)}
                onNotifyMe={canNotifyByEmail ? handleNotifyMeReevaluation : undefined}
            />
        </div>
    );
};

export default FilterBar;
